import { normalize, schema } from 'normalizr'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { lastUtils } from '@last/core'

import api from '@/api.js'
import { subscriptionsApi } from '@/api/client'
import type { LastProductKey } from '@/modules/subscription/types'
import type { Location } from '@/types/location'
import type { Organization } from '@/types/organization'

import { useConfigStore } from './config'

const locationSchema = new schema.Entity('locations')
const organizationSchema = new schema.Entity('organizations', {
  locations: [locationSchema]
})

export const useLocationsStore = defineStore('locations', () => {
  const organizationIds = ref<string[]>([])
  const organizations = ref<Record<string, Organization>>({})
  const locations = ref<Record<string, Location>>({})
  const selectedLocationId = ref<string | null>(
    localStorage.getItem('selectedLocationId')
  )
  const selectedOrganizationId = ref<string | null>(
    localStorage.getItem('selectedOrganizationId') !== 'undefined'
      ? localStorage.getItem('selectedOrganizationId')
      : null
  )

  const locationList = computed<Location[]>(() =>
    Object.values(locations.value)
  )

  const userHasSingleLocation = computed<boolean>(
    () =>
      Object.values(organizations.value).reduce(
        (total, organization) => total + organization.totalLocations,
        0
      ) === 1
  )

  const isSingleLocation = computed<boolean>(() => {
    return (
      organizations.value[selectedOrganizationId.value as string]
        ?.totalLocations === 1
    )
  })

  const isLocationMode = computed<boolean>(() => {
    return !!selectedLocationId.value && !isSingleLocation.value
  })

  const organizationList = computed<Organization[]>(() =>
    organizationIds.value.map(id => ({
      ...organizations.value[id],
      locations: organizations.value[id].locations.map(
        locationId => locations.value[locationId as unknown as string]
      )
    }))
  )

  const selectedLocation = computed<Location | undefined>(() =>
    selectedLocationId.value
      ? locations.value[selectedLocationId.value]
      : undefined
  )

  const selectedOrganization = computed<Organization | undefined>(() => {
    if (!selectedOrganizationId.value) return undefined

    const organization = organizations.value[selectedOrganizationId.value]
    if (!organization) return undefined

    return {
      ...organization,
      locations: (organization?.locations || []).map(
        locationId => locations.value[locationId as unknown as string]
      )
    }
  })

  const currencyIcon = computed<string>(() => {
    return lastUtils.getCurrencyIcon(
      Object.values(locations.value)[0]?.currencyCode
    )
  })

  const lastProducts = computed<{
    location: Record<LastProductKey, boolean>
    organization: Record<LastProductKey, boolean>
  }>(
    (): {
      location: Record<LastProductKey, boolean>
      organization: Record<LastProductKey, boolean>
    } => {
      if (!selectedOrganizationId.value)
        return {
          location: {} as Record<LastProductKey, boolean>,
          organization: {} as Record<LastProductKey, boolean>
        }

      const organizationProducts: Record<LastProductKey, boolean> =
        {} as Record<LastProductKey, boolean>
      let locationProducts: Record<LastProductKey, boolean> = {} as Record<
        LastProductKey,
        boolean
      >

      locationList.value.forEach(location => {
        Object.keys(location.lastProducts).forEach(product => {
          organizationProducts[product as LastProductKey] =
            organizationProducts[product as LastProductKey] ||
            location.lastProducts[product as LastProductKey]
        })
      })

      if (selectedLocation.value)
        locationProducts = selectedLocation.value.lastProducts || {}

      return {
        location: { ...locationProducts },
        organization: { ...organizationProducts }
      }
    }
  )

  async function refreshLocations(): Promise<void> {
    const response = await api.get('/dashboard/locations')
    const normalizedOrganizations = normalize(response.data, [
      organizationSchema
    ])
    locations.value = normalizedOrganizations.entities.locations || {}
    organizations.value = normalizedOrganizations.entities.organizations || {}
    organizationIds.value = normalizedOrganizations.result

    if (!selectedOrganizationId.value) {
      const organization = organizations.value[organizationIds.value[0]]
      selectedOrganizationId.value = organization?.id
      if (selectedOrganizationId.value)
        localStorage.setItem(
          'selectedOrganizationId',
          selectedOrganizationId.value
        )
    }

    if (
      !selectedLocationId.value &&
      (isSingleLocation.value ||
        (!isSingleLocation.value && Object.keys(locations.value).length === 1))
    ) {
      selectedLocationId.value = Object.values(locations.value)[0]?.id
      if (selectedLocationId.value)
        localStorage.setItem('selectedLocationId', selectedLocationId.value)
    }
  }

  async function setSelectedLocationId(locationId: string): Promise<void> {
    if (!locations.value[locationId]) {
      await refreshLocations()
    }

    selectedOrganizationId.value = locations.value[locationId].organizationId
    localStorage.setItem('selectedOrganizationId', selectedOrganizationId.value)
    selectedLocationId.value = locationId
    localStorage.setItem('selectedLocationId', selectedLocationId.value)

    const config = useConfigStore()
    await config.refreshConfig()
    await config.refreshBillingStatus()
  }

  async function setSelectedOrganizationId(
    organizationId: string
  ): Promise<void> {
    selectedLocationId.value = null
    localStorage.removeItem('selectedLocationId')
    selectedOrganizationId.value = organizationId
    localStorage.setItem('selectedOrganizationId', organizationId)

    const config = useConfigStore()
    await config.refreshConfig()
    await config.refreshBillingStatus()
  }

  async function removeSelectedLocation(): Promise<void> {
    selectedLocationId.value = null
    localStorage.removeItem('selectedLocationId')
  }

  async function setSelectedLocationIdByQueryParam(
    locationId: string
  ): Promise<void> {
    selectedLocationId.value = locationId
    localStorage.setItem('selectedLocationId', locationId)
  }

  async function updateLocationPlan({
    locationId,
    plan,
    paymentFrequency
  }: {
    locationId?: string
    plan: string
    paymentFrequency?: string
  }): Promise<{ updated: boolean }> {
    const locId = locationId ?? selectedLocationId.value

    if (!locId) {
      throw new Error('No location ID provided')
    }

    await subscriptionsApi.updatePLan({
      updatePLanRequest: { plan, paymentFrequency }
    })

    const { data: subscription } = await api.get(
      '/dashboard/subscriptions/location'
    )

    locations.value[locId].plan = subscription.plan

    if (subscription.plan === plan) return { updated: true }
    return { updated: false }
  }

  async function updateLocationLastProduct(
    product: LastProductKey,
    active: boolean
  ): Promise<void> {
    if (!selectedLocation.value || !selectedLocationId.value) {
      throw new Error('No location selected')
    }

    selectedLocation.value.lastProducts[product] = active

    await api.put('/dashboard/last-product', {
      locationId: selectedLocationId.value,
      product,
      active
    })
  }

  async function createNewLocation(data: Partial<Location>): Promise<void> {
    await api.post('/dashboard/locations', data)
    await refreshLocations()
  }

  function clear(): void {
    organizationIds.value = []
    organizations.value = {}
    locations.value = {}
    selectedLocationId.value = null
    selectedOrganizationId.value = null
  }

  return {
    // State
    organizationIds,
    organizations,
    locations,
    selectedLocationId,
    selectedOrganizationId,

    // Getters
    locationList,
    userHasSingleLocation,
    isSingleLocation,
    isLocationMode,
    organizationList,
    selectedLocation,
    selectedOrganization,
    currencyIcon,
    lastProducts,

    // Actions
    refreshLocations,
    setSelectedLocationId,
    setSelectedOrganizationId,
    removeSelectedLocation,
    setSelectedLocationIdByQueryParam,
    updateLocationPlan,
    updateLocationLastProduct,
    createNewLocation,
    clear
  }
})
